import { Link } from 'react-router-dom'
import { Eye, Radio, Clock, ChevronRight, ListChecks } from 'lucide-react'
import type { NwsAlert } from '@/lib/nws/types'
import type { ThreatLevel } from '@/lib/criteria/levels'
import type { StormPhase } from '@/lib/storm/classify'
import { watchGuidance, watchHazardChips } from '@/lib/storm/copy'
import { StatusBanner } from '@/features/home/StatusBanner'
import { usePrefs } from '@/store/prefs'
import { fmtTimeClock } from '@/lib/time'
import './storm.css'

/**
 * The Home escalation ladder — a progressive transformation of the home page itself
 * (paired with SkyTint's amber/red page wash):
 *  • QUIET   → the calm StatusBanner, unchanged.
 *  • WATCHING → a prominent amber watch panel (what it means, hazards, what to watch
 *    for, expiry) — NOT a small banner. Weather content stays below.
 *  • STORM   → a red re-enter CTA into the war-room takeover (which also auto-opens).
 */
export function HomeEscalation({
  phase,
  warning,
  watch,
  alerts,
  level,
  locationLabel,
}: {
  phase: StormPhase
  warning: NwsAlert | null
  watch: NwsAlert | null
  alerts: NwsAlert[]
  level: ThreatLevel
  locationLabel: string
}) {
  const clock = usePrefs((s) => s.clock)

  if (phase === 'watching' && watch) {
    // Any concurrent non-watch alerts (advisories/statements) still surface below.
    const others = alerts.filter((a) => a.id !== watch.id)
    return (
      <div className="esc">
        <WatchingHomePanel watch={watch} clock={clock} />
        {others.length > 0 && (
          <StatusBanner level={level} alerts={others} locationLabel={locationLabel} />
        )}
      </div>
    )
  }

  return (
    <div className="esc">
      {phase === 'storm' && warning && (
        <Link to="/storm" className="esc-banner esc-banner--storm" aria-label="Open Storm Mode">
          <span className="esc-banner__pulse">
            <Radio size={18} />
          </span>
          <span className="esc-banner__text">
            <span className="esc-banner__kicker">STORM MODE ACTIVE</span>
            <span className="esc-banner__event">{warning.event}</span>
          </span>
          <ChevronRight size={20} className="esc-banner__chev" />
        </Link>
      )}

      <StatusBanner level={level} alerts={alerts} locationLabel={locationLabel} />
    </div>
  )
}

function WatchingHomePanel({ watch, clock }: { watch: NwsAlert; clock: '12h' | '24h' }) {
  const guide = watchGuidance(watch)
  const chips = watchHazardChips(watch)
  const expiry = watch.ends ?? watch.expires

  return (
    <section className="watch-home" aria-label="Active weather watch">
      <header className="watch-home__top">
        <span className="watch-home__badge">
          <Eye size={14} /> WATCHING
        </span>
        {expiry && (
          <span className="watch-home__until">
            <Clock size={12} /> until {fmtTimeClock(expiry, clock)}
          </span>
        )}
      </header>

      <h2 className="watch-home__event">{watch.event}</h2>
      <p className="watch-home__meaning">{guide.meaning}</p>

      <div className="watch-home__chips">
        {chips.map((c) => (
          <span key={c} className="watch-home__chip">
            {c}
          </span>
        ))}
      </div>

      <div className="watch-home__watchfor">
        <span className="watch-home__watchfor-title">
          <ListChecks size={14} /> What to watch for
        </span>
        <ul className="watch-home__list">
          {guide.watchFor.map((w) => (
            <li key={w}>{w}</li>
          ))}
        </ul>
      </div>

      <Link to="/watching" className="watch-home__more">
        Full watch details <ChevronRight size={15} />
      </Link>
    </section>
  )
}
